import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, FormControl, Select, Typography, RadioGroup, FormControlLabel, Radio, Box, Snackbar, Alert } from '@mui/material';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import api, { fetchCoursesByProfessor } from '../services/api';
import { useAuth } from './AuthContext';
import { useInstitution } from './InstitutionContext';

const AddProfessorDialog = ({
  open,
  onClose,
  departmentId,
  courseId,
  departmentName,
  courseName,
  courseShortName,
  institutionName,
  onAdded,
  onSuccess,
}) => {
  const { user } = useAuth();
  const { selectedInstitution } = useInstitution();
  const [professors, setProfessors] = useState([]);
  const [allProfNames, setAllProfNames] = useState([]);
  const [mode, setMode] = useState('dept');
  const [existingId, setExistingId] = useState('');
  const [newName, setNewName] = useState('');
  const [nameError, setNameError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exampleProf, setExampleProf] = useState('');
  const [profCourses, setProfCourses] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  // track if newName matches an existing professor or submission
  useEffect(() => {
    if (mode !== 'new') {
      setNameError(false);
      return;
    }
    const dup = allProfNames.some(
      (n) => n.trim().toLowerCase() === newName.trim().toLowerCase()
    );
    setNameError(dup);
  }, [newName, mode, allProfNames]);

  useEffect(() => {
    setExistingId('');
  }, [mode, open]);

  useEffect(() => {
    if (open) {
      setMode('dept');
      setExistingId('');
      setNewName('');
      setNameError(false);
      setProfCourses([]);
    }
  }, [open]);

  useEffect(() => {
    if (mode === 'new' || !existingId) {
      setProfCourses([]);
      return;
    }
    fetchCoursesByProfessor(existingId)
      .then((res) => {
        setProfCourses(res.data || []);
      })
      .catch(() => setProfCourses([]));
  }, [existingId, mode]);

  useEffect(() => {
    if (!open) return;
    const instId = user?.userprofile?.institution?.id || selectedInstitution;
    api
      .get('/courses/professor-names/', {
        params: { institution_id: instId },
      })
      .then((res) => setAllProfNames(res.data || []))
      .catch(() => setAllProfNames([]));
  }, [open, user, selectedInstitution]);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        let res;
        if (mode === 'dept' && departmentId) {
          res = await api.get(`/courses/departments/${departmentId}/professors/`);
        } else if (mode === 'inst') {
          const instId = user?.userprofile?.institution?.id;
          if (!instId) return;
          res = await api.get('/courses/professors/', { params: { institution_id: instId } });
        } else {
          return;
        }
        const profs = [...res.data].sort((a, b) => {
          const lastA = a.name.split(' ').slice(-1)[0];
          const lastB = b.name.split(' ').slice(-1)[0];
          return lastA.localeCompare(lastB);
        });
        setProfessors(profs);
        setExampleProf(profs?.[0]?.name || '');
      } catch {
        setProfessors([]);
        setExampleProf('');
      }
    };
    load();
  }, [open, departmentId, mode, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!courseId) return;
    setSubmitting(true);
    try {
      if (mode !== 'new' && existingId) {
        await api.post('/courses/professor-courses/', {
          professor: existingId,
          course: courseId,
        });
        if (onAdded) await onAdded();
      } else if (mode === 'new' && newName) {
        await api.post('/courses/professor-courses/', {
          professor_name: newName,
          course: courseId,
        });
        if (onAdded) await onAdded();
      }
      const msg = 'Professor added';
      if (onSuccess) onSuccess(msg); else setSuccessMessage(msg);
      onClose();
      setExistingId('');
      setNewName('');
      setMode('dept');
    } catch (err) {
      console.error('Failed to add professor:', err.response?.data || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const displayCourse = courseShortName
    ? `${courseShortName} - ${courseName}`
    : courseName;

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle>Add Professor</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Box>
          <Typography variant="body2">{displayCourse}</Typography>
        </Box>
        <FormControl>
          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel value="dept" control={<Radio />} label={`Existing professors from ${departmentName}`} />
            <FormControlLabel value="inst" control={<Radio />} label={`Existing professors from ${institutionName}`} />
            <FormControlLabel value="new" control={<Radio />} label="New" />
          </RadioGroup>
        </FormControl>
        {mode !== 'new' ? (
          <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Professor <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Select value={existingId} onChange={(e) => setExistingId(e.target.value)}>
              {professors.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
            {profCourses.length > 0 && (
              <Typography variant="caption" sx={{ mt: 0.5 }}>
                {profCourses
                  .map((c) => `${c.short_name} (${c.long_name})`)
                  .join(', ')}
              </Typography>
            )}
          </FormControl>
        ) : (
          <div>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Professor Name <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              fullWidth
              placeholder={exampleProf ? `e.g. ${exampleProf}` : ''}
            />
            {nameError && (
              <Typography color="error" variant="body2">
                Professor already exists
              </Typography>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="info" />
        <FilledButton
          type="submit"
          disabled={submitting || (mode==='new' ? !newName || nameError : !existingId)}
          text="Add"
          color="info"
        />
      </DialogActions>
    </Dialog>
    <Snackbar
      open={!!successMessage}
      autoHideDuration={3000}
      onClose={() => setSuccessMessage('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%', fontWeight: 'bold' }}>
        {successMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default AddProfessorDialog;
